import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { PageableFilterRequest } from '../../lib/models/fundamental/types';
import {
  getDividendPayers as getDividendPayersCompanies,
  getHighGrowthCompanies as getGrowthCompanies,
} from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { RootState } from '../../lib/store/index';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import { useSubPageTitle } from '../../hooks';
import LongTermInvestmentsInternal from '../../shared_components/LongTermInvestments/LongTermInvestmentsInternal/LongTermInvestmentsInternal';
import {
  LongTermInvestmentsRow,
} from '../../shared_components/LongTermInvestments/LongTermInvestmentsInternal/LongTermInvestmentsInternal.types';
import { UI_ROUTES } from '../../util/routes';

const DIVIDEND_PAYERS_TAB_INDEX = 0;

const LongTermInvestmentsInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    dividendPayers,
    highGrowthCompanies,
    companyLogoAndName,
    isDividendPayersStatusPending,
    isHighGrowthCompaniesStatusPending,
    getDividendPayers,
    getHighGrowthCompanies,
  } = props;

  const titleRef = useSubPageTitle<HTMLHeadingElement>();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as {
    'navigation-state': { currentTab: number | null, currentTabIndex?: number },
  };
  const { currentTab, currentTabIndex } = locationState['navigation-state'];

  const [activeTabIndex, setActiveTabIndex] = useState<number>(currentTabIndex ?? currentTab ?? 0);
  const [currentDividendPage, setCurrentDividendPage] = useState<number>(0);
  const [currentGrowthPage, setCurrentGrowthPage] = useState<number>(0);

  const isReduction = activeTabIndex !== DIVIDEND_PAYERS_TAB_INDEX;
  const activeContent = isReduction ? highGrowthCompanies.content : dividendPayers.content;

  const rows = useMemo((): LongTermInvestmentsRow[] => activeContent.map(item => ({
    ...item,
    logo: companyLogoAndName[item.symbol]?.logo,
    url: companyLogoAndName[item.symbol]?.url ?? item?.url,
  })), [activeContent, companyLogoAndName]);

  useEffect(() => {
    if (currentTab != null) {
      if (!dividendPayers.content.length && !currentTab) {
        getDividendPayers({ page: 0 });
      }
      if (!highGrowthCompanies.content.length && currentTab) {
        getHighGrowthCompanies({ page: 0 });
      }
    }
  }, [getDividendPayers, getHighGrowthCompanies, dividendPayers, highGrowthCompanies, locationState, currentTab]);

  useEffect(() => {
    if (currentTab == null
      && (!dividendPayers.content.length || !highGrowthCompanies.content.length)
    ) {
      getDividendPayers({ page: 0 });
      getHighGrowthCompanies({ page: 0 });
    }
  }, [dividendPayers, highGrowthCompanies, locationState, getDividendPayers, getHighGrowthCompanies, currentTab]);

  const handleViewMoreClick = () => {
    if (isReduction) {
      getHighGrowthCompanies({ page: currentGrowthPage + 1 });
      setCurrentGrowthPage(currentGrowthPage + 1);
      return;
    }

    getDividendPayers({ page: currentDividendPage + 1 });
    setCurrentDividendPage(currentDividendPage + 1);
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  const hasMorePages = isReduction
    ? highGrowthCompanies.totalPages > currentGrowthPage + 1
    : dividendPayers.totalPages > currentDividendPage + 1;

  return (
    <LongTermInvestmentsInternal
      rows={rows}
      activeTabIndex={activeTabIndex}
      hasMorePages={hasMorePages}
      isViewMoreDisabled={isDividendPayersStatusPending || isHighGrowthCompaniesStatusPending}
      onTabChange={setActiveTabIndex}
      onViewMoreClick={handleViewMoreClick}
      onSymbolClick={handleSymbolClick}
      isAce
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dividendPayers: state.fundamental.dividendPayers,
  highGrowthCompanies: state.fundamental.highGrowthCompanies,
  companyLogoAndName: state.fundamental.companyLogoAndName,
  isDividendPayersStatusPending: isCallStatusPending(
    FundamentalCall.getDividendPayers,
    state.fundamental.statusByCall,
  ),
  isHighGrowthCompaniesStatusPending: isCallStatusPending(
    FundamentalCall.getHighGrowthCompanies,
    state.fundamental.statusByCall,
  ),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getDividendPayers: (params: PageableFilterRequest) => dispatch(getDividendPayersCompanies(params)),
  getHighGrowthCompanies: (params: PageableFilterRequest) => dispatch(getGrowthCompanies(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LongTermInvestmentsInternalWrapper);
